import { compactFlatten, pluralize } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { ReactNode, useRef } from 'react';

import { useFeatureFlags, useScan } from '@kaiizen/kai-pro-api-react-state';
import {
  getTasksInStandardSortOrder,
  getTotalCostIfDefined,
  getTotalRoomAreaIfDefined,
} from '@kaiizen/kai-scan-template-system';

import { ANALYTICS_EVENTS, trackEvent } from '../analytics';
import { CancelIcon } from '../Icons';
import { getApprovedParentChangeOrderScanId } from '../Lib';
import { groupNamePresenter } from '../Presenters';
import { useNavigateToChangeOrder, useScanReviewPageData } from '../State';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../Styles';
import { Button, ScanEditorPageTitleBar, SlimScanEditorPageTitleBar } from '../Widgets';
import { ClusteredTasksEditor } from './ScanEditor/ClusteredTasksEditor';
import { ScanPartNotesButton } from './ScanEditor/ScanPartNotesButton';

const SCAN_REVIEW_STYLE = css`
  label: SCAN_REVIEW_STYLE;
  display: flex;
  flex-direction: column;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const SCAN_REVIEW_CONTENT_STYLE = css`
  label: SCAN_REVIEW_CONTENT_STYLE;
  height: 100%;
  overflow-y: auto;
  scroll-padding-top: ${LAYOUT2.spacing7}px;
`;

const REVIEW_STATUS_STYLE = css`
  label: REVIEW_STATUS_STYLE;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  gap: ${LAYOUT2.spacing1}px;
`;

const BUBBLE_STYLE = css`
  label: BUBBLE_STYLE;
  display: flex;
  border-radius: 100px;
  padding: ${LAYOUT2.spacingHalf}px ${LAYOUT2.spacing1}px;
  ${TYPOGRAPHY2.xSmallBold}
`;

const APPROVED_BUBBLE_STYLE = css`
  label: APPROVED_BUBBLE_STYLE;
  ${BUBBLE_STYLE}
  background: ${COLORS2.alwaysDark.lightGreen};
  color: ${COLORS2.alwaysDark.black};
`;

const EDIT_REQUESTED_BUBBLE_STYLE = css`
  label: EDIT_REQUESTED_BUBBLE_STYLE;
  ${BUBBLE_STYLE}
  background: ${COLORS2.alwaysDark.lightYellow};
  color: ${COLORS2.alwaysDark.black};
`;

const EDIT_RESOLVED_BUBBLE_STYLE = css`
  label: EDIT_RESOLVED_BUBBLE_STYLE;
  ${BUBBLE_STYLE}
  background: ${COLORS2.alwaysDark.lightGray};
  color: ${COLORS2.alwaysDark.black};
`;

const REMAINING_BUBBLE_STYLE = css`
  label: REMAINING_BUBBLE_STYLE;
  ${BUBBLE_STYLE}
  background: ${COLORS2.alwaysDark.lightGray};
  color: ${COLORS2.alwaysDark.black};
`;

const GROUP_HEADER_STYLE = css`
  label: GROUP_HEADER_STYLE;
  position: sticky;
  min-height: ${LAYOUT2.spacing4}px;
  top: 0;
  z-index: 11;
`;

export const ScanReview = ({
  filterRegex,
}: {
  filterRegex?: RegExp;
}) => {
  const { featureFlags } = useFeatureFlags();
  const ref = useRef<HTMLDivElement>(null);
  const {
    baselineScanRoot,
    changesRequestedMode,
    clusterOrder,
    confirm,
    getUnreviewedTasksInGroupCallback,
    groupClusters,
    handleBulkApproveGroup,
    handlePrintPreview,
    approveScope,
    isReady,
    reviewMode,
    scan,
    scanIsEditable,
    scanMutators,
    scanNotesByScanPartId,
    scanRoot,
    scopeApprovalStatus,
    scopedGroups,
    scopedTaskIdsByGroupId,
    startReview,
    submitForReview,
    requestChanges,
    navigateToProject,
  } = useScanReviewPageData({ filterRegex });
  const { scan: baselineScan } = useScan(scan?.parentScanId);
  const navigateToChangeOrder = useNavigateToChangeOrder();

  if (!isReady || !scan || !scopeApprovalStatus || !scanRoot) return null;

  let buttons: ReactNode[] = [];

  const remainingTasksBubbleProps = scopeApprovalStatus.firstRemainingTaskId
    ? {
      style: { cursor: 'pointer' },
      onClick: () => {
        window.location.href = `#task-${scopeApprovalStatus.firstRemainingTaskId}`;
      },
    }
    : {};

  const approvedTasksBubbleProps = scopeApprovalStatus.firstApprovedTaskId
    ? {
      style: { cursor: 'pointer' },
      onClick: () => {
        window.location.href = `#task-${scopeApprovalStatus.firstApprovedTaskId}`;
      },
    }
    : {};

  const editRequestedTasksBubbleProps = scopeApprovalStatus.firstEditRequestedTaskId
    ? {
      style: { cursor: 'pointer' },
      onClick: () => {
        window.location.href = `#task-${scopeApprovalStatus.firstEditRequestedTaskId}`;
      },
    }
    : {};

  const editResolvedTasksBubbleProps = scopeApprovalStatus.firstEditResolvedTaskId
    ? {
      style: { cursor: 'pointer' },
      onClick: () => {
        window.location.href = `#task-${scopeApprovalStatus.firstEditResolvedTaskId}`;
      },
    }
    : {};

  let title: string = `All Scoped Tasks`;
  const scopeOrChangeRequest = scan.scanType === 'changeRequest' ? 'Change Order' : 'Scope';
  const approvedParentChangeOrderScanId = getApprovedParentChangeOrderScanId(scan, baselineScan);

  const isChangeRequest = scan.scanType === 'changeRequest';
  const isApproved = scan.scanLifecycleStatus === 'approved';
  const canEditCurrentProjectWork = !featureFlags.turnBasedHandoff || scanIsEditable;

  const handleDispatch = () =>
    confirm({
      title: 'Dispatch Initial Walk',
      message: 'Are you ready to notify the field agent that the initial walk is ready to start?',
      confirmLabel: 'Yes, Dispatch',
      cancelLabel: 'No, Cancel',
      onConfirm: () => {
        scanMutators.update(scan.id, { scanLifecycleStatus: 'dispatched' })
          .then(() => {
            trackEvent(ANALYTICS_EVENTS.scanDispatched, { scanId: scan.id });
            navigateToProject({ projectId: scan.propertyProjectId });
          });
      },
    });

  buttons = compactFlatten([
    (canEditCurrentProjectWork && isChangeRequest && !isApproved)
      ? (
        <Button
          buttonStyle='yellow'
          onClick={() =>
            confirm({
              title: 'Cancel Change Order',
              message: 'Are you sure you want to cancel this change order? This action cannot be undone.',
              confirmLabel: 'Yes, Cancel It',
              cancelLabel: 'No, Keep It',
              onConfirm: () => {
                scanMutators.cancelChangeOrder(scan.id)
                  .then(() => {
                    trackEvent(ANALYTICS_EVENTS.scanChangeOrderCanceled, { scanId: scan.id });
                    navigateToProject({ projectId: scan.propertyProjectId });
                  });
              },
            })}
        >
          <CancelIcon /> CANCEL CHANGE ORDER
        </Button>
      )
      : undefined,

    (scan.scanLifecycleStatus === 'correctionsRequested' || scan.scanLifecycleStatus === 'pendingReview' || scan.scanLifecycleStatus === 'inReview')
      ? (
        <div css={REVIEW_STATUS_STYLE}>
          {scopeApprovalStatus.approvedTaskCount > 0 && (
            <span {...approvedTasksBubbleProps} css={APPROVED_BUBBLE_STYLE}>{scopeApprovalStatus.approvedTaskCount} Approved</span>
          )}
          {scopeApprovalStatus.editRequestedTaskCount > 0 && (
            <span {...editRequestedTasksBubbleProps} css={EDIT_REQUESTED_BUBBLE_STYLE}>{scopeApprovalStatus.editRequestedTaskCount} Edit Requested</span>
          )}
          {scopeApprovalStatus.editResolvedTaskCount > 0 && (
            <span {...editResolvedTasksBubbleProps} css={EDIT_RESOLVED_BUBBLE_STYLE}>{scopeApprovalStatus.editResolvedTaskCount} Edit Resolved</span>
          )}
          {scopeApprovalStatus.remainingTasksToReviewCount > 0 && (
            <span {...remainingTasksBubbleProps} css={REMAINING_BUBBLE_STYLE}>{scopeApprovalStatus.remainingTasksToReviewCount} Remaining</span>
          )}
        </div>
      )
      : undefined,

    scan.scanLifecycleStatus === 'created' // dispatch button
      ? <Button key='submitForReview' onClick={handleDispatch} buttonStyle={'green'}>DISPATCH INITIAL WALK</Button>
      : undefined,

    canEditCurrentProjectWork && scan.scanLifecycleStatus === 'correctionsRequested' && scopeApprovalStatus.fieldAgentCanSubmitRequestChanges
      ? (
        <Button
          key='requestChanges'
          buttonStyle='gray'
          size='small'
          onClick={() =>
            confirm({
              title: `Re-submit ${scopeOrChangeRequest}`,
              message:
                `Normally the Field Agent is responsible for re-submitting the ${scopeOrChangeRequest}. Are you sure you want to re-submit the ${scopeOrChangeRequest} for review?`,
              confirmLabel: 'Yes, Re-submit',
              cancelLabel: 'No, Cancel',
              onConfirm: () => submitForReview(),
            })}
        >
          FORCE RE-SUBMIT {scopeOrChangeRequest}
        </Button>
      )
      : undefined,

    scopedGroups && scopedGroups.length > 0
      ? (
        <Button
          key='printPreview'
          buttonStyle={'green'}
          onClick={handlePrintPreview}
        >
          {scanIsEditable ? 'PREVIEW' : 'VIEW'} {scopeOrChangeRequest}
        </Button>
      )
      : undefined,

    approvedParentChangeOrderScanId
      ? (
        <Button
          key='viewApprovedChangeOrder'
          buttonStyle='gray'
          onClick={() => navigateToChangeOrder({ scanId: approvedParentChangeOrderScanId })}
        >
          VIEW CHANGE ORDER
        </Button>
      )
      : undefined,

    (scan.scanLifecycleStatus === 'pendingReview') ? <Button key='startReview' onClick={startReview}>START REVIEW</Button> : undefined,

    reviewMode && scopeApprovalStatus.canApprove ? <Button key='approve' onClick={approveScope}>COMPLETE REVIEW: APPROVE</Button> : undefined,
    reviewMode && scopeApprovalStatus.canRequestChanges
      ? <Button key='requestChanges' buttonStyle='yellow' onClick={requestChanges}>Complete Review: REQUEST EDITS</Button>
      : undefined,
  ]);

  return (
    <div css={SCAN_REVIEW_STYLE}>
      <ScanEditorPageTitleBar
        area={getTotalRoomAreaIfDefined(scanRoot)}
        scanLifecycleStatus={scan.scanLifecycleStatus}
        archivedAt={scan.archivedAt}
        cost={getTotalCostIfDefined(scanRoot)}
        title={title}
        buttons={buttons}
      />
      <div css={SCAN_REVIEW_CONTENT_STYLE} ref={ref}>
        {clusterOrder.map(cluster => {
          return groupClusters[cluster].map(group => {
            const scopedTasks = scopedTaskIdsByGroupId[group.id]?.map(id => group.tasksById[id]) ?? [];
            const unapprovedCount = reviewMode ? getUnreviewedTasksInGroupCallback(group).length : 0;
            const groupButtons = [
              <ScanPartNotesButton
                key='notes'
                alwaysDark={true}
                scanPart={group}
                notes={scanNotesByScanPartId[group.id] ?? []}
              />,
              reviewMode && unapprovedCount > 0 && (
                <div style={{ paddingRight: LAYOUT2.spacing1 }}>
                  <Button
                    key='approve-all'
                    buttonStyle='green'
                    size='small'
                    onClick={() => handleBulkApproveGroup(group)}
                  >
                    Approve {pluralize(unapprovedCount, groupNamePresenter(group) + ' task')}
                  </Button>
                </div>
              ),
            ].filter(Boolean);

            const { clusterOrder, taskClusters } = getTasksInStandardSortOrder(group.template, scopedTasks, filterRegex);
            return (
              <>
                <div key={'group-header-' + group.id} css={GROUP_HEADER_STYLE}>
                  <SlimScanEditorPageTitleBar
                    cost={getTotalCostIfDefined(group)}
                    title={groupNamePresenter(group)}
                    buttons={groupButtons}
                  />
                </div>
                <ClusteredTasksEditor
                  key={group.id}
                  baselineTasksById={baselineScanRoot?.groupsById[group.id]?.tasksById}
                  groupId={group.id}
                  specialMode={reviewMode ? 'review' : changesRequestedMode ? 'changesRequested' : undefined}
                  clusterOrder={clusterOrder}
                  taskClusters={taskClusters}
                  stickyTitleTop={LAYOUT2.spacing4}
                />
              </>
            );
          });
        })}
      </div>
    </div>
  );
};
